import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import ContactForm from '../components/ContactForm';
import usePageTitle from '../hooks/usePageTitle';
import { images, services, booking } from '../mock';
import { useBooking } from '../context/BookingContext';
import { ArrowRight, Check, Gift } from 'lucide-react';

const Services = () => {
  usePageTitle('Services');
  const { openBooking } = useBooking();
  return (
    <main>
      {/* Hero */}
      <section className="relative pt-40 pb-24">
        <div className="absolute inset-0">
          <img src={images.cleanModern} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#241033]/95 via-[#2C1743]/85 to-[#43265E]/55" />
        </div>
        <div className="relative max-w-[1280px] mx-auto px-6">
          <Reveal>
            <div className="eyebrow text-silver mb-5">Our Services</div>
            <h1 className="font-display text-5xl md:text-6xl text-white font-medium leading-tight max-w-3xl mb-6">
              Custom-aligned strategy. People-centered support.
            </h1>
            <p className="text-[#D9CEE6] font-light text-lg max-w-2xl">
              We don’t offer one-size-fits-all solutions. Whether you’re building from the ground up, refining a vision, or navigating a major transition, our services meet you where you are—and help you move forward with clarity, confidence, and care.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Gift note */}
      <section className="py-14 bg-[#43265E]">
        <div className="max-w-[1000px] mx-auto px-6 flex items-center gap-5 justify-center text-center">
          <Gift className="w-6 h-6 text-[#D9CEE6] shrink-0" strokeWidth={1.5} />
          <p className="text-[#E7E2EE] font-light text-lg">
            Book a session for yourself—or gift a consultation to someone you believe in. Sometimes the best thing you can offer is <span className="italic text-white">a partner who sees the vision and knows how to help make it real.</span>
          </p>
        </div>
      </section>

      {/* Services list */}
      <section className="py-24 bg-[#FBFAF8]">
        <div className="max-w-[1280px] mx-auto px-6 space-y-24">
          {services.map((s, i) => (
            <Reveal key={s.id}>
              <div className={`grid lg:grid-cols-2 gap-14 items-center ${i % 2 === 1 ? 'lg:[direction:rtl]' : ''}`}>
                <div className="lg:[direction:ltr]">
                  <div className="relative">
                    <div className="absolute -inset-3 bg-silver rounded-sm opacity-50" />
                    <img src={s.image} alt={s.title} className="relative w-full h-[420px] object-cover rounded-sm" />
                  </div>
                </div>
                <div className="lg:[direction:ltr]">
                  <div className="eyebrow text-[#7E5C9E] mb-4">{s.group}</div>
                  <h2 className="font-display text-4xl md:text-5xl text-[#241033] font-medium leading-tight mb-6">{s.title}</h2>
                  <p className="text-[#4A4453] font-light text-lg leading-relaxed mb-8">{s.desc}</p>
                  <ul className="space-y-3 mb-10">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-[#EDE8F2] flex items-center justify-center shrink-0">
                          <Check className="w-3.5 h-3.5 text-[#43265E]" strokeWidth={2} />
                        </span>
                        <span className="text-[#4A4453] font-light">{p}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => openBooking(s.link, s.group)} className="btn-plum rounded-full px-9 py-3.5 text-sm tracking-luxe uppercase inline-flex items-center gap-2">
                    Book Now <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* AI & Workflow Automation */}
      <section className="py-24 bg-[#241033]" data-testid="services-ai-card">
        <div className="max-w-[1280px] mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={images.aiWorking} alt="Intelligent Systems & Process Automation" className="w-full h-[380px] object-cover object-top rounded-sm" />
          </Reveal>
          <Reveal delay={150}>
            <div className="eyebrow text-silver mb-4">Intelligent Systems & Process Automation</div>
            <h2 className="font-display text-4xl md:text-5xl text-white font-medium leading-tight mb-6">Automate the Process. Elevate the People.</h2>
            <p className="text-[#D9CEE6] font-light text-lg leading-relaxed mb-10">
              Custom applications, purposeful websites, workflow automation, and responsible AI integration—designed around your people, so technology makes work better for them.
            </p>
            <Link to="/services/ai-workflow-automation" data-testid="services-ai-learn-more" className="btn-ivory rounded-full px-9 py-3.5 text-sm tracking-luxe uppercase inline-flex items-center gap-2">
              Explore This Service <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Contact */}
      <section className="py-28 bg-[#F4F1EC]">
        <div className="max-w-[1280px] mx-auto px-6 grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <div className="eyebrow text-[#7E5C9E] mb-6">Contact Us</div>
            <h2 className="font-display text-4xl md:text-5xl text-[#241033] font-medium leading-tight mb-6">We can’t wait to hear from you.</h2>
            <p className="text-[#4A4453] font-light text-lg">Fill out some info and we will be in touch shortly to explore how we can be an answer.</p>
          </Reveal>
          <Reveal delay={150}>
            <div className="bg-white rounded-sm p-8 md:p-10 shadow-[0_30px_60px_-40px_rgba(36,16,51,0.4)]">
              <ContactForm />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Services;
